import { gql } from '@apollo/client';

/**
 * Branding settings (Domain) GraphQL operations.
 * Used by the BrandingSettingsPage native migration.
 *
 * Wire contract reference: optisigns-open-api `claude-dev/ted-documents/04-branding-setting/developer-guide.md`.
 *
 * IMPORTANT — social-login fields are stored INVERTED:
 *   The backend negates `isAllow*LoginSocial` on write so the DB stores `true` meaning
 *   "allow". The frontend negates again on read to display "is-hide" semantics. Both
 *   ends inverting is symmetric (no-op logically) but preserves scio1 wire format.
 *   See spec.md "Social login double inversion".
 */

/** Full Domain field selection used by the Branding Settings page. */
const DOMAIN_FIELDS = `
  _id
  accountId
  siteTitle
  host
  subHost
  favicon
  logo
  logoCompany
  backgoundLogin
  loginBox
  loginBoxColor
  loginBoxPadding
  loginBoxOpacity
  loginBoxMarginLeftRight
  loginBoxMarginTopBottom
  colorTheme
  email
  emailFrom
  emailFullname
  presenterUrl
  removeOptiSignsBranding
  hideProofofPlay
  hideAuditLogging
  hideMonitorAndAlert
  hidePaymentHistoryPages
  hideSubscriptionPages
  hideCreateAccountButton
  hideExternalCommunications
  hideAccountMembersPage
  isAllowGoogleLoginSocial
  isAllowFBLoginSocial
  isAllowMSLoginSocial
  isVerifiedEmail
  isActiveHost
  isNotParentUpdate
  isChildHost
  customBrandCanvas
  customBrandCanva
  hideOptiSignsBrandedApps
  appManagement
  appManagementForShareLinks
  templateCategories
`;

export const GET_MY_DOMAIN = gql`
  query GetMyDomain {
    getMyDomain {
      ${DOMAIN_FIELDS}
    }
  }
`;

/**
 * Returns Boolean — refetch GET_MY_DOMAIN after save to pick up server-side
 * changes (image keys, isVerifiedEmail mirror, isNotParentUpdate flip, etc.).
 */
export const UPDATE_DOMAIN = gql`
  mutation UpdateDomain($payload: UpdateDomainInput!) {
    updateDomain(payload: $payload)
  }
`;

/**
 * Subdomain availability check.
 *
 * Returns `null` when the caller already owns a subHost (no need to re-check).
 * Otherwise: `{status, host?, message?}` where status is:
 *   0 = reserved/forbidden (e.g. "app")
 *   1 = available
 *   2 = owned by another account (parent's host echoed back for the inherit-warning copy)
 */
export const CHECK_SUBDOMAIN_BRANDING = gql`
  query CheckSubDomainBranding($subHost: String!) {
    checkSubDomainbranding(subHost: $subHost) {
      status
      host
      message
    }
  }
`;

/**
 * Activate `{subHost}.optisigns.net` for branding. Accepts the full UpdateDomainInput
 * so initial settings can save in the same round-trip. If status=2 (parent-owned),
 * this account becomes a child host. Always fires customer.io white-label resync.
 */
export const ACTIVATE_SUBHOST_BRANDING = gql`
  mutation ActivateSubHostBranding($payload: UpdateDomainInput!) {
    activateSubHostBranding(payload: $payload) {
      status
      message
    }
  }
`;

/**
 * Custom CNAME availability check. Returns `{status: true}` when available.
 */
export const CHECK_DOMAIN_BRANDING = gql`
  query CheckDomainBranding($host: String!) {
    checkDomainbranding(host: $host) {
      status
      message
    }
  }
`;

/**
 * Activate a custom CNAME host. First-time activation sends an ops alert email
 * for manual SSL provisioning (24-48h after activation). If owned by another
 * account, this account becomes a child host (no alert).
 */
export const ACTIVATE_HOST_BRANDING = gql`
  mutation ActivateHostBranding($host: String!) {
    activateHostBranding(host: $host) {
      status
      message
    }
  }
`;

/**
 * Re-check SES verification of the branding email.
 *
 * If newly verified (was false → now true), the backend mirrors `isVerifiedEmail=true`
 * onto both the `Domain` row and `company.branding`. Returns the **current** verified
 * state. Bug-fix vs SCIO 1.0: returns `true` correctly when already verified (scio1
 * incorrectly returned `false` in that branch).
 *
 * No polling — call when the user clicks a "Refresh" button after they've clicked the
 * verify link in their inbox.
 */
export const VERIFY_EMAIL_DOMAIN_SES = gql`
  query VerifyEmailDomainSES {
    verifyEmailDomainSES
  }
`;

/**
 * Set the custom branding email + display name.
 *
 * If `email` is new or changed, triggers SES `sendCustomVerificationEmail` —
 * the user gets a verification link in their inbox. `isVerifiedEmail` is reset
 * to `false` until they click the link.
 *
 * If only `emailFullname` changed, no SES call is made and `isVerifiedEmail`
 * is preserved.
 *
 * Returns the **previous** `isVerifiedEmail` value — useful for showing a
 * confirm dialog if the user is breaking out of an already-verified state.
 */
export const UPDATE_VERIFY_EMAIL_DOMAIN_SES = gql`
  mutation UpdateVerifyEmailDomainSES($payload: VerifyEmailInput!) {
    updateVerifyEmailDomainSES(payload: $payload)
  }
`;

/**
 * Canvas template-categories list — server-side proxy to the AI Templates metadata
 * service (`smallapp.optisigns.com/api/ai-templates/metadata/categories`). Backend
 * maps the upstream `{_id, name, parent_id, sort_order}` rows to the legacy
 * `{id, title, parent, sort_order}` shape so the FE contract is unchanged.
 *
 * Resilience: backend swallows errors and returns `[]` on any HTTP failure —
 * so a transient outage on the metadata service doesn't block rendering the branding page.
 */
export const GET_CANVAS_CATEGORIES = gql`
  query GetCanvasCategories {
    getCanvasCategories {
      id
      title
      parent
      sort_order
    }
  }
`;

/**
 * Update the per-account "show/hide apps" allow-list used by the branding picker.
 *
 * `appIds` is a complete replacement set — empty list means "no apps allowed". Pass
 * `forShareLinks: false` to write `domain.appManagement`; `forShareLinks: true` to
 * write `domain.appManagementForShareLinks` (the share-link variant).
 *
 * Returns `true` on success. Backend upserts the Domain row if missing.
 */
export const UPDATE_DOMAIN_APPS = gql`
  mutation UpdateDomainApps($appIds: [String!]!, $forShareLinks: Boolean!) {
    updateDomainApps(appIds: $appIds, forShareLinks: $forShareLinks)
  }
`;
